import { useMemo, useState } from 'react';

import { useForwardCurveChartSeries } from '@/features/charts';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';
import BasicChartBlock from '@/features/markets/components/Chart/BasicChartBlock';
import ButtonSection from '@/features/markets/components/Chart/ForwardCurveChartBlock/ButtonSection';
import useInViewport from '@/shared/hooks/useInViewport';
import type { TemplateChartSeries } from '@/shared/types/charts';

type ForwardCurveChartType = {
  layout?: { fullWidth?: boolean; height?: number };
  templateChartSeries?: TemplateChartSeries[];
};

const mapWithReturnedTransactionDate = (
  comparisonTemplateSeries: TemplateChartSeries[],
  chartSeries: ExtendedSeriesOptionsType[] | undefined
) => {
  return comparisonTemplateSeries.map((item, i) => {
    if (!chartSeries?.[i + 1]) return item;
    return {
      ...item,
      symbol: {
        ...item.symbol,
        transactionDate: chartSeries[i + 1].custom.transactionDate
      }
    };
  });
};

const ForwardCurveChartBlock = ({ layout, templateChartSeries }: ForwardCurveChartType) => {
  const [containerRef, isInViewport] = useInViewport();
  const [comparisonTemplateSeries, setComparisonTemplateSeries] = useState<TemplateChartSeries[]>(
    []
  );

  const { loading, error, chartSeries } = useForwardCurveChartSeries(
    templateChartSeries && [...templateChartSeries, ...comparisonTemplateSeries],
    setComparisonTemplateSeries,
    isInViewport
  );

  const mappedComparisonTemplate = useMemo(() => {
    return mapWithReturnedTransactionDate(comparisonTemplateSeries, chartSeries);
  }, [chartSeries, comparisonTemplateSeries]);
  const buttonSection = useMemo(() => {
    if (!chartSeries?.length) return null;
    return (
      <header className='flex justify-end'>
        <ButtonSection
          chartSeries={chartSeries}
          comparisonTemplateSeries={mappedComparisonTemplate}
          setComparisonTemplateSeries={setComparisonTemplateSeries}
          templateChartSeries={templateChartSeries}
        />
      </header>
    );
  }, [templateChartSeries, mappedComparisonTemplate, setComparisonTemplateSeries, chartSeries]);

  return (
    <BasicChartBlock
      buttonSection={buttonSection}
      chartSeries={chartSeries}
      chartType='forwardSimple'
      containerRef={containerRef}
      error={error}
      label='Forward curve prices'
      layout={layout}
      loading={loading}
    />
  );
};

export default ForwardCurveChartBlock;
